import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faPlay,
  faCamera,
  faFloppyDisk,
  faArrowRotateLeft,
  faArrowRotateRight,
  faDesktop,
  faSquareH,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import {
  closeProfileModal,
  openProfileModal,
  selectIsProfileModalOpen,
} from "../../store/common/profileModalSlice";
import { ChromePicker } from "react-color";
import {
  setGenerateMode,
  setPreviewMode,
  setVideoGenerateWarningMessage,
} from "../../store/common/uiSlice";
import { useSelector } from "react-redux";
import { saveProject, userLogout } from "../../thunks/dashboardThunks";
import {
  setIsOnlyAvatar,
  setProjectCategory,
  setProjectName,
} from "../../store/project/projectSlice";
import MenuButton from "../common/MenuButton";
import { CanvasContext, useCanvasContext } from "./CanvasContext";
import "react-circular-progressbar/dist/styles.css";
import { useNavigate, useParams } from "react-router-dom";
import GenerateVideoPopover from "../common/popovers/GenerateVideoPopover";
import { Tooltip } from "../common/Tooltip";
import { CategoryDropdown } from "../common/CategoryDropdown";
import UserProfileModal from "../../pages/Profile";
import { generateOnlyAvatarVideo } from "../../api/templates";
import { generateOnlyAvatarVideoFun } from "../../thunks/editor/canvasThunks";
import { setShowGrid } from "../../store/editor/GridCanvasSlice";
import { addBackgroundColor } from "../../canvasUtils";
import { createAIDubbingVideo } from "../../thunks/aiDubbing/aiDubbingThunk";
import { getFromLocal } from "../../utils/localStorage";

const TopBar = ({ onDashboardRedirect }) => {
  // console.log("editor topbar", aiDubbing);
  const isThisOnlyAvatar = window.location.pathname.includes("only-avatar");
  const aiDubbing = window.location.pathname.includes("ai-dubbing");
  const { setBackgroundColor } = useCanvasContext(); // Destructure the setBackgroundColor function from the CanvasContext

  const [selectedColor, setSelectedColor] = useState("#ffffff"); // Default to white color

  const handleColorChange = (e) => {
    const color = e.target.value;
    setSelectedColor(color);
    // Update local state
    setBackgroundColor(color); // Update background color in the context
  };

  return (
    <div className="bg-white px-4 py-2 shadow-md flex justify-between items-center">
      <DropdownButton
        onDashboardRedirect={isThisOnlyAvatar ? false : onDashboardRedirect}
      />
      <ActionButtons />
      <RightIcons
        selectedColor={selectedColor} // Pass selectedColor as prop
        handleColorChange={handleColorChange}
        aiDubbing // Pass handleColorChange as prop
      />
    </div>
  );
};

function DropdownButton({ onDashboardRedirect }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { name, isOnlyAIVoice } = useSelector((state) => state.project);
  const aiDubbing = window.location.pathname.includes("ai-dubbing");

  const dispatch = useDispatch();

  const debouncedSaveProject = useMemo(
    () =>
      debounce((projName) => {
        // Dispatch your action here, passing the projectName as needed
        dispatch(setProjectName(projName));
        dispatch(saveProject(id, false));
      }, 2000),
    [dispatch]
  );

  const handleProjectNameChange = (e) => {
    const updatedProjectName = e.target.value;
    dispatch(setProjectName(updatedProjectName));
  };

  const [isRecording, setIsRecording] = useState(false);
  const [videoURL, setVideoURL] = useState("");
  const [gridModalOpen, setShowGridShowModalOpen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [color, setColor] = useState("#fff"); // Default background color
  const [showColorPicker, setShowColorPicker] = useState(false);

  const ref = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowGridShowModalOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const gridCanvasItem = [
    {
      image: "/tool/dashboard/static/images/ShowGrid.png",
      text: "Show Grid",
      action: () => dispatch(setShowGrid(true)),
    },

    {
      image: "/tool/dashboard/static/images/HideGrid.png",
      text: "Hide Grid",
      action: () => dispatch(setShowGrid(false)),
    }, // Replace with logout action
  ];

  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
    } else {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { mediaSource: "screen" },
        });

        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data);
          }
        };
        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(recordedChunksRef.current, {
            type: "video/mp4",
          });
          const url = URL.createObjectURL(blob);
          setVideoURL(url);
          recordedChunksRef.current = [];
        };
        mediaRecorderRef.current.start();

        setIsRecording(true);
      } catch (err) {
        console.error("Error: ", err);
      }
    }
  };
  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleColorChange = async (color) => {
    await addBackgroundColor(canvas, { color: color }, dispatch);
    setColor(color);
  };
  const { canvas } = useContext(CanvasContext);

  return (
    <div className="flex items-center space-x-1">
      <img
        className="w-10 cursor-pointer"
        src="/tool/dashboard/static/images/logo-192.png"
        alt="Logo"
        onClick={() =>
          onDashboardRedirect ? onDashboardRedirect() : navigate("/")
        }
      />
      {!aiDubbing && (
        <div className="pl-2">
          <FontAwesomeIcon icon={faPen} />
          <input
            type="text"
            value={name}
            onChange={handleProjectNameChange}
            onBlur={(e) => debouncedSaveProject(e.target.value)}
            className="p-2 text-gray-600 font-semibold border-b border-solid border-gray-500 "
          />
        </div>
      )}
      {!isOnlyAIVoice && !aiDubbing && (
        <div>
          <div className="w-1/2 px-2">
            <input
              type="color"
              className="border rounded-full w-8 h-9 overflow-hidden"
              value={color}
              // style={{ padding: "-10px" }}
              onChange={(e) => handleColorChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {!isOnlyAIVoice && !aiDubbing && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
          }}
        >
          <div title="Profile" className="relative" ref={ref}>
            <button
              onClick={() => setShowGridShowModalOpen(!gridModalOpen)}
              className="p-1 border border-gray-300 rounded cursor-pointer flex items-center gap-10"
            >
              <img
                className="w-10 cursor-pointer border-gray"
                src="/tool/dashboard/static/images/Grid.png"
                alt="gridIcon"
                style={{
                  height: "20px",
                  width: "20px",
                }}
              />
            </button>
            {gridModalOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                {gridCanvasItem.map((item, index) => (
                  <div
                    key={index}
                    className="p-4 hover:bg-gray-200 hover:text-gray-800 cursor-pointer flex flex-row items-center gap-2"
                    onClick={item.action}
                  >
                    <img
                      className="w-10 cursor-pointer border-gray"
                      src={item.image}
                      alt="gridIcon"
                      style={{
                        height: "20px",
                        width: "20px",
                      }}
                    />
                    {item.text}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            style={{
              color: "#11142D",
              padding: "5px 5px",
              border: "1px solid #F4F3F6",
              borderRadius: "5px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
            onClick={toggleRecording}
          >
            <img
              className="w-10 cursor-pointer border-gray"
              src={
                isRecording
                  ? "/tool/dashboard/static/images/stop-record.png"
                  : "/tool/dashboard/static/images/record.png"
              }
              alt="Logo"
              style={{
                height: "20px",
                width: "20px",
              }}
            />
          </button>

          {videoURL && (
            <div
              style={{
                color: "#11142D",
                padding: "5px 5px",
                border: "1px solid #F4F3F6",
                borderRadius: "5px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <img
                className="w-10 cursor-pointer border-gray"
                src="/tool/dashboard/static/images/download-icon.png"
                alt="Logo"
                // height={"10px"}
                // width={"10px"}
                style={{
                  height: "20px",
                  width: "20px",
                }}
              />

              <a href={videoURL} download="recording.mp4">
                Download Recording
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionButtons() {
  const iconButtons = [
    faArrowRotateLeft,
    faArrowRotateRight,
    faDesktop,
    faSquareH,
  ];
  return (
    <div className="flex space-x-3">
      {/* {iconButtons.map((icon, index) => (
        <button key={index} className="p-2 hover:bg-gray-100 rounded">
          <FontAwesomeIcon icon={icon} />
        </button>
      ))} */}
    </div>
  );
}

function RightIcons({ selectedColor, handleColorChange }) {
  const { id } = useParams();
  const trial = useSelector((state) => state?.user?.details?.data?.trial);
  // const videoLength = useSelector(
  //   (state) => state?.user?.details?.data?.videos
  // );

  const videoLength = getFromLocal("videos");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isProfileModalOpen = useSelector(selectIsProfileModalOpen);
  const { slides } = useSelector((state) => state.slides);

  const { subTab } = useSelector((state) => state.ui);
  const isProAvatarTab = useMemo(() => subTab === 2, [subTab]);
  const appState = useSelector((state) => state.appState);
  const { type, isOnlyAvatar, avatar, isOnlyAIVoice } = useSelector(
    (state) => state.project
  );

  const userTemplateType = useMemo(() => type === "user-template", [type]);
  const [showPopup, setShowPopup] = useState(false);
  const [showGeneratePopup, setShowGeneratePopup] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { target_language, selectedVideo } = useSelector(
    (state) => state.aiDubbing
  );
  const isonlyavtar = window.location.pathname.includes("only-avatar");
  useEffect(() => {
    if (isonlyavtar) {
      dispatch(setIsOnlyAvatar(true));
    }
  }, []);
  const aiDubbing = window.location.pathname.includes("ai-dubbing");

  console.log("aiDubbing", aiDubbing);
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onPreviewClick = () => {
    dispatch(setPreviewMode(true));
  };

  const onGenerateClick = () => {
    if (trial && videoLength === 0) {
      setShowGeneratePopup(true);
      dispatch(
        setVideoGenerateWarningMessage(
          "You can create only one video in the Free Plan"
        )
      );
      return;
    }
    const voice = slides[0].generatedVoice;
    const voiceFile = slides[0].voiceFile;

    const proAvatar = avatar?.url?.includes("hd_avatars") ? true : undefined;
    const customAvatar = avatar?.url?.includes("user_data") ? true : undefined;
    const parmas = {
      voice: voiceFile ? voiceFile : voice,
      avatar: avatar,
      pro_avatar: proAvatar,
      user_avatar: customAvatar,
    };
    if (isonlyavtar) {
      setShowGeneratePopup(true);

      if (parmas?.voice === "" || parmas.voice === null) {
        dispatch(setVideoGenerateWarningMessage("please add script first"));
        return;
      }

      dispatch(generateOnlyAvatarVideoFun(parmas));
      return;
    }
    dispatch(saveProject(id, false));

    dispatch(setGenerateMode(true));

    setShowGeneratePopup(true);
  };

  const GenerateAiDubbingFun = async () => {
    const userId = getFromLocal("userId");
    const data = {
      project_id: id,
      user_id: userId,
      // file: selectedVideo,
      source_lang: "auto",
      drop_background_audio: false,
      target_lang: target_language,
    };
    dispatch(setGenerateMode(true));

    setShowGeneratePopup(true);
    dispatch(createAIDubbingVideo(data, navigate));
  };

  const handleOpenModal = (e) => {
    e.stopPropagation();
    dispatch(openProfileModal());
  };

  const handleLogout = () => {
    const callback = () => (window.location.href = "/");

    dispatch(userLogout(callback));
  };

  const handleSaveClick = () => {
    if (userTemplateType) {
      dispatch(saveProject(id, true));
    } else {
      setShowPopup(true);
    }
  };

  const handleCloseModal = () => {
    dispatch(closeProfileModal());
  };

  const dropdownItems = [
    { text: "Profile", action: handleOpenModal },
    // { text: "Settings", action: () => {} }, // Replace with settings action
    { text: "Logout", action: handleLogout }, // Replace with logout action
  ];

  function onGeneratetionAudio() {
    const baseUrl = process.env.REACT_APP_SERVER_API_URL;
    const voice = slides[0].generatedVoice.mp3;
    const fullUrl = `${baseUrl}${voice}`;

    // Create an anchor element
    const link = document.createElement("a");
    link.href = fullUrl;
    link.target = "_blank";
    link.download = "audio"; // Optional: Specify the file name here

    // Append the anchor to the body
    document.body.appendChild(link);

    // Trigger the download by simulating a click
    link.click();

    // Remove the anchor from the document
    document.body.removeChild(link);
  }

  return (
    <>
      {trial && (
        <p className="text-gray-700">
          Some of the features may not be available to you. Please upgrade to
          gain access to the restricted features
        </p>
      )}
      <div className="flex items-center space-x-3">
        {/* <input
          type="color"
          value={selectedColor}
          onChange={handleColorChange}  
        /> */}
        {!isOnlyAvatar && !isOnlyAIVoice && !aiDubbing && (
          <button
            title="Preview"
            // disabled={true}
            className="relative p-2 hover:bg-gray-100 rounded group"
            onClick={onPreviewClick}
          >
            <FontAwesomeIcon icon={faPlay} className="mr-2" />
            Preview
            {/* <Tooltip className={"top-full !z-10 w-full"}>
            Preview will be released on 17/05/2024
          </Tooltip> */}
          </button>
        )}

        <button title="Generate" className="hover:bg-gray-100 rounded">
          <MenuButton
            icon={"/tool/dashboard/static/images/icons/tools/generate.svg"}
            iconOn={"/tool/dashboard/static/images/icons/tools/generate-on.svg"}
            onClick={
              isOnlyAIVoice
                ? onGeneratetionAudio
                : aiDubbing
                ? GenerateAiDubbingFun
                : onGenerateClick
            }
            text={
              isOnlyAIVoice
                ? "Generate Audio"
                : aiDubbing
                ? "Generate AI Dubbing Video"
                : "Generate Video"
            }
          />
        </button>

        {!isOnlyAvatar && !isOnlyAIVoice && !aiDubbing && (
          <button
            title={appState === "saving" ? "Saving" : "Save"}
            className={`p-2 hover:bg-vb-highlight rounded ${
              appState === "saving" ? "bg-gray-100 cursor-not-allowed" : ""
            } relative group`}
            disabled={appState === "saving"}
            onClick={handleSaveClick}
          >
            <FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />
            {showPopup && (
              <CategoryDropdown
                onSave={() => {
                  setShowPopup(false);
                  dispatch(saveProject(id, true));
                }}
                onClose={() => {
                  setShowPopup(false);
                }}
                className="absolute right-0"
              />
            )}
            Save Template
          </button>
        )}

        <div title="Profile" className="relative" ref={ref}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="p-2 hover:bg-gray-100 rounded"
          >
            <FontAwesomeIcon icon={faUserCircle} className="text-gray-700" />
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50">
              {dropdownItems.map((item, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-gray-200 hover:text-gray-800 cursor-pointer"
                  onClick={item.action}
                >
                  {item.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showGeneratePopup && (
        <GenerateVideoPopover togglePopup={() => setShowGeneratePopup(false)} />
      )}
      {isProfileModalOpen && <UserProfileModal onClose={handleCloseModal} />}
    </>
  );
}

export default TopBar;
